import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { EXTRALARGE_BREAKPOINT_WIDTH } from 'ish-core/configurations/injection-keys';
import { FocusOutsideDirective } from 'ish-core/directives/focus-outside.directive';
import { InjectSingle } from 'ish-core/utils/injection';

/**
 * CUSTOMIZATION: Password input with show/hide + Clear (FR1.1.2 / FR1.1.3),
 * for reactive forms outside Formly (e.g. Request access).
 */
@Component({
  selector: 'prs-password-input',
  templateUrl: './presidio-password-input.component.html',
  styleUrls: ['./presidio-password-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [FocusOutsideDirective, ReactiveFormsModule, NgClass, TranslatePipe],
})
export class PresidioPasswordInputComponent implements OnInit, OnDestroy {
  @Input({ required: true }) control!: FormControl<string | null>;
  @Input() inputId = 'password';
  @Input() labelKey = 'account.register.password.label';
  @Input() autocomplete: 'current-password' | 'new-password' | 'off' = 'new-password';
  @Input() ariaDescribedBy: string | undefined;
  @Input() ariaInvalid: boolean | null = null;
  @Input() testingId: string | undefined;

  private extralargeBreakpointWidth =
    inject<InjectSingle<typeof EXTRALARGE_BREAKPOINT_WIDTH>>(EXTRALARGE_BREAKPOINT_WIDTH);
  private cdr = inject(ChangeDetectorRef);
  private valueSub?: Subscription;

  showPassword = false;
  showButton = false;

  private isTouchDevice = !SSR && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  private revealButton: boolean;

  constructor() {
    this.revealButton = this.isTouchDevice
      ? window.matchMedia(`(min-width: ${this.extralargeBreakpointWidth}px)`).matches
      : true;
  }

  get canClear(): boolean {
    return !!this.control?.value;
  }

  ngOnInit(): void {
    this.syncRevealFromValue(this.control?.value);
    this.valueSub = this.control.valueChanges.subscribe(value => {
      this.syncRevealFromValue(value);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
  }

  onInput(): void {
    const value = this.control.value;
    if (this.revealButton && String(value || '').length === 1) {
      this.showButton = true;
    }
    if (!value) {
      this.showButton = false;
      this.showPassword = false;
    }
    this.cdr.markForCheck();
  }

  onFocusOutside(isFocusedOutside: boolean): void {
    if (isFocusedOutside) {
      this.showPassword = false;
      this.showButton = false;
      this.cdr.markForCheck();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /** FR1.1.3 — explicit clear of the password field. */
  clearPassword(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.control.setValue('');
    this.control.markAsPristine();
    this.control.markAsUntouched();
    this.showPassword = false;
    this.showButton = false;
    this.cdr.markForCheck();
  }

  private syncRevealFromValue(value: string | null | undefined): void {
    if (!value) {
      this.showButton = false;
      this.showPassword = false;
      return;
    }
    if (this.revealButton && String(value).length >= 1) {
      this.showButton = true;
    }
  }
}
